import { z } from "zod";
import { PROGRAMME_BODY_KINDS, PROGRAMME_BODY_MEMBER_ROLES, PROGRAMME_MEETING_STATUSES, } from "../constants/programme-body-states.js";
export const createProgrammeBodySchema = z.object({
    name: z.string().min(1).max(200),
    kind: z.enum(PROGRAMME_BODY_KINDS),
    description: z.string().max(2000).optional(),
    collaborationSiteId: z.string().uuid().optional(),
    torObjectKind: z.enum(["document", "workspace_document"]).nullable().optional(),
    torObjectId: z.string().uuid().nullable().optional(),
    l30ContactId: z.string().uuid().nullable().optional(),
    artefactFolderId: z.string().uuid().nullable().optional(),
});
export const updateProgrammeBodySchema = createProgrammeBodySchema.partial();
export const createProgrammeBodyMemberSchema = z.object({
    contactId: z.string().uuid(),
    role: z.enum(PROGRAMME_BODY_MEMBER_ROLES),
    orgLabel: z.string().max(200).optional(),
});
export const updateProgrammeBodyMemberSchema = z.object({
    role: z.enum(PROGRAMME_BODY_MEMBER_ROLES).optional(),
    orgLabel: z.string().max(200).nullable().optional(),
});
export const programmeBodySchema = z.object({
    id: z.string().uuid(),
    engagementId: z.string().uuid(),
    name: z.string().min(1).max(200),
    kind: z.enum(PROGRAMME_BODY_KINDS),
    description: z.string().max(2000).nullable().default(null),
    collaborationSiteId: z.string().uuid().nullable().default(null),
    torObjectKind: z.enum(["document", "workspace_document"]).nullable().default(null),
    torObjectId: z.string().uuid().nullable().default(null),
    l30ContactId: z.string().uuid().nullable().default(null),
    artefactFolderId: z.string().uuid().nullable().default(null),
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: z.string().email().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const programmeBodyMemberSchema = z.object({
    id: z.string().uuid(),
    bodyId: z.string().uuid(),
    contactId: z.string().uuid(),
    role: z.enum(PROGRAMME_BODY_MEMBER_ROLES),
    orgLabel: z.string().max(200).nullable().default(null),
    // Joined from `contacts` at read time (same convention as
    // `PortalProgrammeBodySchema.chairName`) — never stored on the member
    // row itself. Nullable only for the edge case of a contact having been
    // hard-deleted out from under an existing membership row; UI falls back
    // to the raw contactId when null.
    contactName: z.string().nullable().default(null),
    contactEmail: z.string().nullable().default(null),
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: z.string().email().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// List-response envelopes matching the `{ bodies }` / `{ members }` shapes
// returned by `packages/api/src/routes/programme-bodies.ts` (Task 5). Kept
// here (not ad-hoc in the web client) per 04-shared/ssot.md R6 — every
// `apiFetch` call validates against a shared schema, same convention as
// `PortalWorkspaceListResponseSchema` / `ContactListResponseSchema`.
export const programmeBodyListResponseSchema = z.object({
    bodies: z.array(programmeBodySchema),
});
export const programmeBodyMemberListResponseSchema = z.object({
    members: z.array(programmeBodyMemberSchema),
});
// ─── Portal read projection (Task 11) ──────────────────────
//
// Read-only, engagement-scoped projection surfaced on the portal plan
// view (see `EngagementPlanResponseSchema.programmeBodies` in
// ./engagement-plan.js). Deliberately thin: kind + chair name only —
// no membership roster, no attendance, no minutes body text. Portal
// users get a "who's leading this" read, not the staff admin surface.
export const PortalProgrammeBodySchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    kind: z.enum(PROGRAMME_BODY_KINDS),
    // Null when the body has no active chair yet.
    chairName: z.string().nullable(),
    // Count of approved minutes across this body's meetings (membership
    // detail surfaces the text; plan teaser only shows the count).
    approvedMinutesCount: z.number().int().nonnegative().default(0),
});
// ─── Portal Committees & working groups (membership-first read) ─
//
// Richer than `PortalProgrammeBodySchema` (plan teaser). Fed by the
// same `programme_bodies` / members / meetings tables staff configure
// under `/programme-collaboration`. Both kinds; seated members only.
export const PortalWorkingGroupMemberSchema = z.object({
    name: z.string(),
    role: z.enum(PROGRAMME_BODY_MEMBER_ROLES),
    orgLabel: z.string().nullable(),
});
export const PortalWorkingGroupAgendaItemSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    notes: z.string().nullable(),
    paperDocumentId: z.string().uuid().nullable(),
    paperTitle: z.string().nullable(),
});
export const PortalWorkingGroupApprovedMinutesSchema = z.object({
    body: z.string(),
    approvedAt: z.string().datetime().nullable(),
});
export const PortalWorkingGroupSessionSchema = z.object({
    id: z.string().uuid(),
    title: z.string().nullable(),
    startsAt: z.string().datetime(),
    location: z.string().nullable(),
    /** First agenda item title when present; otherwise null. */
    agendaFocus: z.string().nullable(),
    status: z.enum(PROGRAMME_MEETING_STATUSES),
    agendaItems: z.array(PortalWorkingGroupAgendaItemSchema).default([]),
    approvedMinutes: PortalWorkingGroupApprovedMinutesSchema.nullable().default(null),
});
export const PortalWorkingGroupActionSchema = z.object({
    id: z.string().uuid(),
    description: z.string(),
    dueAt: z.string().datetime().nullable(),
    status: z.enum(["open", "completed"]),
    ownerName: z.string().nullable(),
});
export const PortalWorkingGroupTorSchema = z.object({
    objectKind: z.enum(["document", "workspace_document"]),
    objectId: z.string().uuid(),
    title: z.string(),
});
/**
 * A document in the body's artefact folder. `kind` is a short display
 * label derived from the MIME type ("PDF", "Word", …), not a stored
 * enum — loose per the response-schema policy.
 */
export const PortalWorkingGroupArtefactSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    kind: z.string(),
});
export const PortalWorkingGroupListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(200),
    kind: z.enum(PROGRAMME_BODY_KINDS),
    purpose: z.string().nullable(),
    memberCount: z.number().int().nonnegative(),
    nextSession: PortalWorkingGroupSessionSchema.nullable(),
    linkedArtefactCount: z.number().int().nonnegative().default(0),
});
export const PortalWorkingGroupDetailSchema = PortalWorkingGroupListItemSchema.extend({
    members: z.array(PortalWorkingGroupMemberSchema),
    pastSessions: z.array(PortalWorkingGroupSessionSchema),
    artefacts: z.array(PortalWorkingGroupArtefactSchema).default([]),
    tor: PortalWorkingGroupTorSchema.nullable().default(null),
    l30ContactName: z.string().nullable().default(null),
    actions: z.array(PortalWorkingGroupActionSchema).default([]),
});
export const PortalWorkingGroupListResponseSchema = z.object({
    workingGroups: z.array(PortalWorkingGroupListItemSchema),
});
/** Route params for membership-gated body document download. */
export const PortalWorkingGroupDocumentParamSchema = z.object({
    id: z.string().uuid(),
    documentId: z.string().uuid(),
});
//# sourceMappingURL=programme-bodies.js.map